import css from "./TextToSongWidget.sass?inline"
import {Html} from "@opendaw/lib-dom"
import {createElement} from "@opendaw/lib-jsx"
import {Option, UUID} from "@opendaw/lib-std"
import {Promises} from "@opendaw/lib-runtime"
import {StudioService} from "@/service/StudioService"
import {
    AudioContentFactory,
    Project,
    ProjectMeta,
    ProjectProfile
} from "@opendaw/studio-core"
import {InstrumentFactories} from "@opendaw/studio-adapters"
import {AudioFileBox} from "@opendaw/studio-boxes"
import {generateSong, separateStems, fetchAudioAsArrayBuffer, MUSIC_MODELS} from "@/ui/ai-copilot/AIService"
import {getCreditsInfo, useCredit, totalRemaining, onCreditsChange, trackAnalyticsApiCall} from "@/ui/credits/CreditStore"
import {showCheckoutModal} from "@/ui/credits/CheckoutModal"

const className = Html.adoptStyleSheet(css, "TextToSongWidget")

type Step = {
    label: string
    status: "pending" | "active" | "done" | "error"
}

const SUGGESTIONS = [
    "Dark trap beat with 808 bass and hi-hats",
    "Chill lo-fi hip hop with piano and vinyl crackle",
    "High energy EDM drop with supersaw leads",
    "Drill beat with sliding 808s and dark melodies",
    "Ambient phonk with cowbells and reverb",
    "Future bass with bright plucks and vocal chops"
]

export const TextToSongWidget = ({service}: {service: StudioService}) => {
    const textarea: HTMLTextAreaElement = (
        <textarea placeholder="Describe your song... e.g. 'Dark trap beat with 808 bass and haunting melodies'" rows="2"/>
    ) as unknown as HTMLTextAreaElement

    const modelSelect: HTMLSelectElement = (
        <select>
            {MUSIC_MODELS.map(m => <option value={m.id} selected={m.id === "minimax"}>{m.name}</option>)}
        </select>
    ) as unknown as HTMLSelectElement

    const generateBtn: HTMLButtonElement = (
        <button className="generate-btn">Generate <span className="btn-arrow">&#8594;</span></button>
    ) as unknown as HTMLButtonElement

    const creditsDisplay: HTMLDivElement = <div className="credits-display"/> as unknown as HTMLDivElement

    function updateCreditsDisplay(): void {
        const info = getCreditsInfo()
        if (info.unlimited) {
            creditsDisplay.textContent = "Unlimited plan"
            creditsDisplay.classList.remove("low", "empty")
        } else {
            const total = info.daily + info.purchased
            if (info.purchased > 0) {
                creditsDisplay.textContent = `${info.daily} free + ${info.purchased} purchased`
            } else {
                creditsDisplay.textContent = `${info.daily}/${info.dailyLimit} free today`
            }
            creditsDisplay.classList.toggle("low", total <= 3 && total > 0)
            creditsDisplay.classList.toggle("empty", total <= 0)
        }
        const total = totalRemaining()
        generateBtn.disabled = total <= 0
        if (total <= 0) {
            generateBtn.textContent = "No credits left"
            generateBtn.classList.add("buy-mode")
            generateBtn.onclick = () => showCheckoutModal()
        } else {
            generateBtn.textContent = ""
            const arrow = document.createElement("span")
            arrow.className = "btn-arrow"
            arrow.innerHTML = "&#8594;"
            generateBtn.textContent = "Generate "
            generateBtn.appendChild(arrow)
            generateBtn.classList.remove("buy-mode")
            generateBtn.onclick = () => doGenerate()
        }
    }

    updateCreditsDisplay()
    onCreditsChange(updateCreditsDisplay)
    const progressArea: HTMLDivElement = <div className="progress-area" style={{display: "none"}}/> as unknown as HTMLDivElement
    const stepList: HTMLDivElement = <div className="step-list"/> as unknown as HTMLDivElement
    const progressBar: HTMLDivElement = <div className="progress-bar"/> as unknown as HTMLDivElement
    const progressFill: HTMLDivElement = <div className="progress-fill" style={{width: "0%"}}/> as unknown as HTMLDivElement

    progressBar.appendChild(progressFill)
    progressArea.appendChild(stepList)
    progressArea.appendChild(progressBar)

    const steps: Step[] = [
        {label: "Generating song with AI...", status: "pending"},
        {label: "Splitting into stems...", status: "pending"},
        {label: "Importing vocals track...", status: "pending"},
        {label: "Importing drums track...", status: "pending"},
        {label: "Importing bass track...", status: "pending"},
        {label: "Importing other track...", status: "pending"},
        {label: "Creating project & opening...", status: "pending"}
    ]

    function renderSteps(): void {
        stepList.innerHTML = ""
        for (const step of steps) {
            const iconEl = document.createElement("span")
            iconEl.className = "step-icon"
            if (step.status === "active") {
                const spinner = document.createElement("span")
                spinner.className = "spinner"
                iconEl.appendChild(spinner)
            } else if (step.status === "done") {
                iconEl.textContent = "\u2713"
            } else if (step.status === "error") {
                iconEl.textContent = "\u2717"
            } else {
                iconEl.textContent = "\u25CB"
            }
            const labelEl = document.createElement("span")
            labelEl.textContent = step.label
            const row = document.createElement("div")
            row.className = `step ${step.status}`
            row.appendChild(iconEl)
            row.appendChild(labelEl)
            stepList.appendChild(row)
        }
    }

    function setStep(index: number, status: Step["status"]): void {
        steps[index].status = status
        renderSteps()
        const doneCount = steps.filter(s => s.status === "done").length
        const pct = Math.round((doneCount / steps.length) * 100)
        progressFill.style.width = `${pct}%`
    }

    function resetSteps(): void {
        for (const s of steps) s.status = "pending"
        renderSteps()
        progressFill.style.width = "0%"
    }

    async function importAudioToProject(project: Project, name: string, arrayBuffer: ArrayBuffer): Promise<void> {
        const {status, value: sample, error} = await Promises.tryCatch(
            service.sampleService.importFile({name, arrayBuffer})
        )
        if (status === "rejected") {
            console.warn(`Failed to import '${name}'`, error)
            return
        }
        const uuid = UUID.parse(sample.uuid)
        await Promises.tryCatch(service.sampleManager.getAudioData(uuid))
        project.editing.modify(() => {
            const {trackBox, instrumentBox} = project.api.createInstrument(InstrumentFactories.Tape)
            instrumentBox.label.setValue(name)
            const audioFileBox = project.boxGraph.findBox<AudioFileBox>(uuid)
                .unwrapOrElse(() => AudioFileBox.create(project.boxGraph, uuid, box => {
                    box.fileName.setValue(name)
                    box.startInSeconds.setValue(0)
                    box.endInSeconds.setValue(sample.duration)
                }))
            AudioContentFactory.createNotStretchedRegion({
                boxGraph: project.boxGraph, sample, audioFileBox, position: 0, targetTrack: trackBox
            })
        })
    }

    let overlayStepContainer: HTMLDivElement | null = null
    let overlayProgressFill: HTMLDivElement | null = null
    let overlayStatusText: HTMLDivElement | null = null
    let overlayPctText: HTMLDivElement | null = null
    let pulseRing: HTMLDivElement | null = null
    let waveCanvas: HTMLCanvasElement | null = null
    let waveAnimId: number | null = null

    function injectOverlayStyles(): void {
        if (document.getElementById("tts-overlay-styles")) return
        const style = document.createElement("style")
        style.id = "tts-overlay-styles"
        style.textContent = `
            @keyframes tts-pulse { 0%,100% { transform:scale(1); opacity:0.5; } 50% { transform:scale(1.3); opacity:0; } }
            @keyframes tts-spin { to { transform:rotate(360deg); } }
            @keyframes tts-fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
            @keyframes tts-glow { 0%,100% { box-shadow:0 0 40px rgba(160,80,255,0.3); } 50% { box-shadow:0 0 80px rgba(160,80,255,0.5),0 0 120px rgba(200,100,255,0.2); } }
            @keyframes tts-shimmer { 0% { transform:translateX(-100%); } 100% { transform:translateX(200%); }  }
            @keyframes tts-float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
            .tts-overlay { position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.92);backdrop-filter:blur(20px);will-change:opacity; }
            .tts-overlay canvas { position:absolute;bottom:0;left:0;width:100%;height:140px;opacity:0.4;will-change:transform; }
            .tts-orb-bg { position:absolute;width:320px;height:320px;border-radius:50%;background:radial-gradient(circle,rgba(160,80,255,0.2),transparent 70%);animation:tts-pulse 3s ease-in-out infinite;pointer-events:none;will-change:transform,opacity; }
            .tts-card { position:relative;display:flex;flex-direction:column;align-items:center;gap:20px;padding:2.5em 3em;border-radius:24px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);min-width:420px;max-width:500px;animation:tts-glow 4s ease-in-out infinite,tts-fadeUp 0.5s cubic-bezier(0.22,1,0.36,1); }
            .tts-spinner-ring { width:64px;height:64px;border-radius:50%;position:relative;display:flex;align-items:center;justify-content:center;will-change:transform; }
            .tts-spinner-ring::before { content:'';position:absolute;inset:0;border-radius:50%;border:3px solid transparent;border-top-color:hsl(270,85%,60%);border-right-color:hsl(320,75%,55%);animation:tts-spin 1.2s cubic-bezier(0.4,0,0.2,1) infinite;will-change:transform; }
            .tts-spinner-ring .tts-icon { font-size:24px;animation:tts-float 2.5s ease-in-out infinite; }
            .tts-pulse-ring { position:absolute;inset:-8px;border-radius:50%;border:2px solid rgba(160,80,255,0.3);animation:tts-pulse 2.5s ease-in-out infinite;will-change:transform,opacity; }
            .tts-title { font-size:18px;font-weight:700;color:white;text-align:center;letter-spacing:-0.01em; }
            .tts-status { font-size:13px;color:rgba(255,255,255,0.5);text-align:center;min-height:1.2em;transition:color 0.5s ease,opacity 0.5s ease; }
            .tts-pct { font-size:32px;font-weight:800;background:linear-gradient(135deg,hsl(270,85%,60%),hsl(320,75%,55%));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;transition:opacity 0.4s ease; }
            .tts-progress-track { width:100%;height:4px;background:rgba(255,255,255,0.06);border-radius:4px;overflow:hidden; }
            .tts-progress-fill { height:100%;background:linear-gradient(90deg,hsl(270,85%,58%),hsl(320,75%,55%),hsl(200,80%,55%));background-size:200% 100%;border-radius:4px;transition:width 0.8s cubic-bezier(0.22,1,0.36,1);position:relative;will-change:width; }
            .tts-progress-fill::after { content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent);animation:tts-shimmer 2s ease-in-out infinite;will-change:transform; }
            .tts-steps { width:100%;display:flex;flex-direction:column;gap:4px; }
            .tts-step { display:flex;align-items:center;gap:10px;font-size:12px;padding:6px 10px;border-radius:8px;transition:color 0.4s ease,background-color 0.4s ease,opacity 0.4s ease;color:rgba(255,255,255,0.25); }
            .tts-step.active { color:white;background:rgba(160,80,255,0.1); }
            .tts-step.done { color:hsl(160,65%,50%); }
            .tts-step.error { color:hsl(0,70%,60%); }
            .tts-step-icon { width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0; }
            .tts-step-spinner { width:14px;height:14px;border:2px solid rgba(255,255,255,0.1);border-top-color:hsl(270,85%,60%);border-radius:50%;animation:tts-spin 0.8s cubic-bezier(0.4,0,0.2,1) infinite;will-change:transform; }

        `
        document.head.appendChild(style)
    }



    function startWaveAnimation(canvas: HTMLCanvasElement): void {
        const ctx = canvas.getContext("2d")
        if (!ctx) return
        const dpr = window.devicePixelRatio || 1
        let cachedW = 0, cachedH = 0
        function resize() {
            cachedW = canvas.offsetWidth
            cachedH = canvas.offsetHeight
            canvas.width = cachedW * dpr
            canvas.height = cachedH * dpr
            ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
        }
        resize()
        const ro = new ResizeObserver(resize)
        ro.observe(canvas)
        let startTime = performance.now()
        const draw = (now: number) => {
            const t = (now - startTime) * 0.06
            ctx.clearRect(0, 0, cachedW, cachedH)
            for (let wave = 0; wave < 3; wave++) {
                ctx.beginPath()
                const amp = 12 + wave * 6
                const freq = 0.008 + wave * 0.003
                const speed = 0.02 + wave * 0.01
                const alpha = 0.15 - wave * 0.04
                const step = 3
                for (let x = 0; x <= cachedW; x += step) {
                    const y = cachedH / 2 + Math.sin(x * freq + t * speed + wave) * amp + Math.sin(x * freq * 2.5 + t * speed * 1.5) * amp * 0.3
                    if (x === 0) ctx.moveTo(x, y)
                    else ctx.lineTo(x, y)
                }
                ctx.lineTo(cachedW, cachedH)
                ctx.lineTo(0, cachedH)
                ctx.closePath()
                const grad = ctx.createLinearGradient(0, 0, cachedW, 0)
                grad.addColorStop(0, `rgba(160,80,255,${alpha})`)
                grad.addColorStop(0.5, `rgba(200,100,255,${alpha + 0.05})`)
                grad.addColorStop(1, `rgba(100,150,255,${alpha})`)
                ctx.fillStyle = grad
                ctx.fill()
            }
            waveAnimId = requestAnimationFrame(draw)
        }
        waveAnimId = requestAnimationFrame(draw)
    }

    function mountOverlay(): HTMLDivElement {
        injectOverlayStyles()
        const overlay = document.createElement("div")
        overlay.className = "tts-overlay"
        overlay.animate([{opacity: 0}, {opacity: 1}], {duration: 400, easing: "ease-out"})

        const orb = document.createElement("div")
        orb.className = "tts-orb-bg"
        overlay.appendChild(orb)

        waveCanvas = document.createElement("canvas")
        overlay.appendChild(waveCanvas)
        startWaveAnimation(waveCanvas)

        const card = document.createElement("div")
        card.className = "tts-card"

        const spinnerRing = document.createElement("div")
        spinnerRing.className = "tts-spinner-ring"
        pulseRing = document.createElement("div")
        pulseRing.className = "tts-pulse-ring"
        spinnerRing.appendChild(pulseRing)
        const icon = document.createElement("span")
        icon.className = "tts-icon"
        icon.textContent = "\u266B"
        spinnerRing.appendChild(icon)
        card.appendChild(spinnerRing)

        const title = document.createElement("div")
        title.className = "tts-title"
        title.textContent = "Creating your song..."
        card.appendChild(title)

        overlayPctText = document.createElement("div")
        overlayPctText.className = "tts-pct"
        overlayPctText.textContent = "0%"
        card.appendChild(overlayPctText)

        overlayStatusText = document.createElement("div")
        overlayStatusText.className = "tts-status"
        overlayStatusText.textContent = "Initializing..."
        card.appendChild(overlayStatusText)

        const track = document.createElement("div")
        track.className = "tts-progress-track"
        overlayProgressFill = document.createElement("div")
        overlayProgressFill.className = "tts-progress-fill"
        overlayProgressFill.style.width = "0%"
        track.appendChild(overlayProgressFill)
        card.appendChild(track)

        overlayStepContainer = document.createElement("div")
        overlayStepContainer.className = "tts-steps"
        card.appendChild(overlayStepContainer)

        overlay.appendChild(card)
        document.body.appendChild(overlay)
        buildOverlaySteps()
        return overlay
    }

    let overlayStepRows: {row: HTMLDivElement, icon: HTMLSpanElement, label: HTMLSpanElement, lastStatus: string}[] = []

    function buildOverlaySteps(): void {
        if (!overlayStepContainer) return
        overlayStepRows = []
        for (const step of steps) {
            const row = document.createElement("div")
            row.className = `tts-step ${step.status}`
            const iconEl = document.createElement("span")
            iconEl.className = "tts-step-icon"
            const label = document.createElement("span")
            label.textContent = step.label
            row.appendChild(iconEl)
            row.appendChild(label)
            overlayStepContainer.appendChild(row)
            overlayStepRows.push({row, icon: iconEl, label, lastStatus: ""})
        }
        renderOverlaySteps()
    }

    function renderOverlaySteps(): void {
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i]
            const cached = overlayStepRows[i]
            if (!cached || cached.lastStatus === step.status) continue
            cached.lastStatus = step.status
            cached.row.className = `tts-step ${step.status}`
            cached.icon.innerHTML = ""
            if (step.status === "active") {
                const sp = document.createElement("span")
                sp.className = "tts-step-spinner"
                cached.icon.appendChild(sp)
            } else if (step.status === "done") {
                cached.icon.textContent = "\u2713"
            } else if (step.status === "error") {
                cached.icon.textContent = "\u2717"
            } else {
                cached.icon.textContent = "\u25CB"
            }
        }
    }

    let currentDisplayPct = 0
    let pctAnimId: number | null = null

    function animatePctTo(target: number): void {
        if (pctAnimId !== null) cancelAnimationFrame(pctAnimId)
        const start = currentDisplayPct
        const startTime = performance.now()
        const duration = 600
        const tick = (now: number) => {
            const elapsed = Math.min((now - startTime) / duration, 1)
            const eased = 1 - Math.pow(1 - elapsed, 3)
            currentDisplayPct = Math.round(start + (target - start) * eased)
            if (overlayPctText) overlayPctText.textContent = `${currentDisplayPct}%`
            if (elapsed < 1) {
                pctAnimId = requestAnimationFrame(tick)
            } else {
                pctAnimId = null
            }
        }
        pctAnimId = requestAnimationFrame(tick)
    }

    function syncOverlay(): void {
        const doneCount = steps.filter(s => s.status === "done").length
        const pct = Math.round((doneCount / steps.length) * 100)
        if (overlayProgressFill) overlayProgressFill.style.width = `${pct}%`
        animatePctTo(pct)
        const activeStep = steps.find(s => s.status === "active")
        if (overlayStatusText && activeStep) overlayStatusText.textContent = activeStep.label
        renderOverlaySteps()
    }

    function setStepWithSync(index: number, status: Step["status"]): void {
        setStep(index, status)
        syncOverlay()
    }

    async function doGenerate(): Promise<void> {
        const prompt = textarea.value.trim()
        if (!prompt) return

        if (totalRemaining() <= 0) {
            showCheckoutModal()
            return
        }

        useCredit()

        updateCreditsDisplay()
        generateBtn.disabled = true
        resetSteps()

        const project = Project.new(service)
        const songName = prompt.length > 40 ? prompt.substring(0, 40) + "..." : prompt
        service.projectProfileService.setValue(
            Option.wrap(new ProjectProfile(UUID.generate(), project, ProjectMeta.init(songName), Option.None))
        )
        // Navigate to studio and switch to default screen
        import("@opendaw/lib-jsx").then(({RouteLocation}) => RouteLocation.get().navigateTo("/studio"))
        service.switchScreen("default")

        const overlay = mountOverlay()
        syncOverlay()

        try {
            setStepWithSync(0, "active")
              const modelId = modelSelect.value
              trackAnalyticsApiCall("/api/replicate/predictions", "replicate-musicgen")
              const audioUrl = await generateSong(modelId, prompt, 30, 120, true)
            setStepWithSync(0, "done")

            setStepWithSync(1, "active")
            const stems = await separateStems(audioUrl)
            setStepWithSync(1, "done")

            const stemEntries: [string, string, number][] = [
                ["Vocals", stems.vocals, 2],
                ["Drums", stems.drums, 3],
                ["Bass", stems.bass, 4],
                ["Other", stems.other, 5]
            ]

            for (const [name, url, stepIdx] of stemEntries) {
                setStepWithSync(stepIdx, "active")
                try {
                    const buffer = await fetchAudioAsArrayBuffer(url)
                    await importAudioToProject(project, name, buffer)
                    setStepWithSync(stepIdx, "done")
                } catch (e) {
                    console.warn(`Failed to import ${name}:`, e)
                    setStepWithSync(stepIdx, "error")
                }
            }

            setStepWithSync(6, "active")
            setStepWithSync(6, "done")

        } catch (e: any) {
            console.error("Text-to-Song error:", e)
            for (let i = 0; i < steps.length; i++) {
                if (steps[i].status === "active") setStepWithSync(i, "error")
            }
        } finally {
            updateCreditsDisplay()
            if (waveAnimId !== null) cancelAnimationFrame(waveAnimId)
            waveAnimId = null
            if (pctAnimId !== null) cancelAnimationFrame(pctAnimId)
            pctAnimId = null
            currentDisplayPct = 0
            overlay.animate([{opacity: 1}, {opacity: 0}], {duration: 600, easing: "ease"}).onfinish = () => {
                overlay.remove()
                overlayStepContainer = null
                overlayProgressFill = null
                overlayStatusText = null
                overlayPctText = null
            }
        }
    }

    generateBtn.onclick = () => {
        if (totalRemaining() <= 0) {
            showCheckoutModal()
        } else {
            doGenerate()
        }
    }

    const suggestionsEl: HTMLDivElement = <div className="suggestions"/> as unknown as HTMLDivElement
    const suggestionsLabel: HTMLSpanElement = <span className="suggestions-label">or try:</span> as unknown as HTMLSpanElement
    suggestionsEl.appendChild(suggestionsLabel)
    for (const suggestion of SUGGESTIONS) {
        const btn: HTMLButtonElement = <button>{suggestion}</button> as unknown as HTMLButtonElement
        btn.onclick = () => {
            textarea.value = suggestion
            textarea.focus()
        }
        suggestionsEl.appendChild(btn)
    }

    return (
        <div className={className}>
            <div className="prompt-card">
                {textarea}
                <div className="toolbar">
                    {modelSelect}
                    <div className="spacer"/>
                    {creditsDisplay}
                    {generateBtn}
                </div>
            </div>
            {suggestionsEl}
        </div>
    )
}
